// src/pages/schooladmin/Settings.js
import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Container, Row, Col, Card, Button, Form, InputGroup, Badge,
  Spinner, Tabs, Tab, Table, Modal, ProgressBar, ButtonGroup
} from "react-bootstrap";
import dayjs from "dayjs";
import Swal from "sweetalert2";
import {
  collection, query, where, orderBy, onSnapshot, doc, setDoc, updateDoc,
  addDoc, deleteDoc, writeBatch, serverTimestamp, getDocs
} from "firebase/firestore";
import { getAuth, onAuthStateChanged } from "firebase/auth";
import { getStorage, ref as sRef, uploadBytesResumable, getDownloadURL } from "firebase/storage";
import { db } from "../../services/firebase";
import useSchool from "../../hooks/useSchool";
import { storage } from "../../services/firebase"; // ✅ your pre-configured storage (gs://photos-oysi5)
/* ---------------- helpers / defaults ---------------- */
const toDate = (v) => (v?.toDate ? v.toDate() : v ? new Date(v) : null);
const clamp = (n, a, b) => Math.max(a, Math.min(b, n));
const safeClone = (obj) => (typeof structuredClone === "function" ? structuredClone(obj) : JSON.parse(JSON.stringify(obj)));

const STATUSES = [
  { key: "present", label: "Present", code: "P", variant: "success" },
  { key: "absent", label: "Absent", code: "A", variant: "danger" },
  { key: "late", label: "Late", code: "L", variant: "warning" },
  { key: "excused", label: "Excused", code: "E", variant: "info" },
  { key: "halfday", label: "Half-day", code: "H", variant: "secondary" },
];

const DEFAULT_PREFS = {
  schoolDisplayName: "",
  phone: "",
  email: "",
  website: "",
  address: "",
  logoURL: "",
  academicYearStart: null,
  academicYearEnd: null,

  // Attendance
  attendance: {
    workingDays: [1,2,3,4,5],      // 0=Sun … 6=Sat
    lockDays: 2,                   // how many days back editing is allowed
    markStart: "08:00",
    markEnd: "14:00",
    statuses: STATUSES.map(s => s.key),
  },

  // Online classes default
  online: {
    defaultPlatform: "meet",       // meet|zoom|teams|other
  },

  // Circulars & notifications prefs
  comms: {
    allowTeacherCirculars: false,
    requireAck: false,
    enablePush: false,
    enableEmail: false,
  },

  tagsSuggested: ["holiday","exam","event"],
  updatedAt: null,
  createdAt: null,
};

const CLASS_FALLBACK = [
  ...Array.from({ length: 12 }, (_, i) => `Class ${i + 1}`),
  "Foundation 6","Foundation 7","Foundation 8","Foundation 9","Foundation 10",
  "NEET","JEE","Class 11 NEET","Class 11 JEE","Class 12 NEET","Class 12 JEE",
  "Integrated","LT",
];
const SECTION_FALLBACK = ["A","B","C","D","E"];

/* ---------------- Modals ---------------- */
function ManageSectionsModal({ show, onHide, schoolId, className }) {
  const [rows, setRows] = useState([]);
  const [newSec, setNewSec] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!schoolId || !className || !show) return;
    const col = collection(db, "schools", schoolId, "classes");
    const unsub = onSnapshot(
      query(col, where("className", "==", className), orderBy("section", "asc")),
      (snap) => {
        setRows(snap.docs.map(d => ({ id: d.id, ...(d.data() || {}) })));
      }
    );
    return () => unsub();
  }, [schoolId, className, show]);

  const addSection = async () => {
    const s = newSec.trim();
    if (!s) return;
    setSaving(true);
    try {
      if (rows.some(r => (r.section || "").toLowerCase() === s.toLowerCase())) {
        Swal.fire({ icon: "info", title: "Section exists" });
      } else {
        await addDoc(collection(db, "schools", schoolId, "classes"), {
          className,
          section: s,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
        });
        setNewSec("");
      }
    } finally {
      setSaving(false);
    }
  };

  const removeSection = async (r) => {
    const ok = await Swal.fire({
      icon: "warning", title: "Remove section?",
      text: `${className}-${r.section}`, showCancelButton: true, confirmButtonColor: "#d33"
    });
    if (!ok.isConfirmed) return;
    await deleteDoc(doc(db, "schools", schoolId, "classes", r.id));
  };

  return (
    <Modal show={show} onHide={onHide} centered>
      <Modal.Header closeButton><Modal.Title>Manage Sections · {className}</Modal.Title></Modal.Header>
      <Modal.Body>
        <div className="d-flex gap-2 mb-3">
          <Form.Control
            placeholder="New section (e.g., F)"
            value={newSec}
            onChange={(e) => setNewSec(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addSection(); } }}
          />
          <Button onClick={addSection} disabled={saving}>Add</Button>
        </div>

        <Table hover size="sm" className="align-middle">
          <thead><tr><th style={{width: 80}}>#</th><th>Section</th><th className="text-end">Actions</th></tr></thead>
          <tbody>
            {rows.length === 0 ? (
              <tr><td colSpan={3} className="text-muted text-center py-3">No sections</td></tr>
            ) : rows.map((r, idx) => (
              <tr key={r.id}>
                <td>{idx+1}</td>
                <td className="fw-semibold">{r.section}</td>
                <td className="text-end">
                  <Button size="sm" variant="outline-danger" onClick={() => removeSection(r)}>
                    <i className="bi bi-trash" /> Remove
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
        <div className="small text-muted">Removing a section does not delete students; ensure records are updated.</div>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="outline-secondary" onClick={onHide}>Close</Button>
      </Modal.Footer>
    </Modal>
  );
}

/* ---------------- Main page ---------------- */
export default function SchoolSettings() {
  const { school, loading: loadingSchool } = useSchool();

  const [meRole, setMeRole] = useState(null);
  const [, setMe] = useState(null);

  useEffect(() => {
    const auth = getAuth();
    const off = onAuthStateChanged(auth, (u) => {
      setMe(u || null);
      if (!u) { setMeRole(null); return; }
      const unsub = onSnapshot(doc(db, "users", u.uid), (snap) => {
        setMeRole((snap.data() || {}).role || null);
      });
      return () => unsub();
    });
    return () => off();
  }, []);

  const canEdit = useMemo(() => {
    const r = String(meRole || "").toLowerCase();
    return ["superadmin","schooladmin","admin","school","school_admin"].includes(r);
  }, [meRole]);

  /* ----- base school doc (for hydration & optional mirroring) ----- */
  const [baseSchool, setBaseSchool] = useState(null);
  const [loadingBase, setLoadingBase] = useState(true);

  useEffect(() => {
    if (!school?.id) return;
    setLoadingBase(true);
    const unsub = onSnapshot(
      doc(db, "schools", school.id),
      (snap) => { setBaseSchool({ id: snap.id, ...(snap.data() || {}) }); setLoadingBase(false); },
      () => setLoadingBase(false)
    );
    return () => unsub();
  }, [school?.id]);

  /* ----- prefs (schools/{id}/settings/prefs) ----- */
  const [prefs, setPrefs] = useState(DEFAULT_PREFS);
  const [loadingPrefs, setLoadingPrefs] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    if (!school?.id) return;
    setLoadingPrefs(true);
    const ref = doc(db, "schools", school.id, "settings", "prefs");
    const unsub = onSnapshot(ref, (snap) => {
      const d = snap.data();
      if (d) {
        setPrefs({
          ...DEFAULT_PREFS,
          ...d,
          academicYearStart: toDate(d.academicYearStart),
          academicYearEnd: toDate(d.academicYearEnd),
        });
      } else {
        setPrefs(DEFAULT_PREFS);
      }
      setLoadingPrefs(false);
      setDirty(false);
    }, () => setLoadingPrefs(false));
    return () => unsub();
  }, [school?.id]);

  // Hydrate prefs with any missing fields from base doc IF the user hasn't started editing
  useEffect(() => {
    if (!baseSchool || dirty) return;
    setPrefs(prev => {
      const next = safeClone(prev);
      next.schoolDisplayName = next.schoolDisplayName || baseSchool.name || "";
      next.logoURL = next.logoURL || baseSchool.logoURL || "";
      next.website = next.website || baseSchool.website || "";
      next.phone = next.phone || baseSchool.phone || "";
      next.email = next.email || baseSchool.email || "";
      next.address = next.address || baseSchool.address || "";
      return next;
    });
  }, [baseSchool, dirty]);

  const change = (path, value) => {
    setPrefs(prev => {
      const next = safeClone(prev);
      const parts = path.split(".");
      let obj = next;
      for (let i=0;i<parts.length-1;i++) obj = obj[parts[i]];
      obj[parts.at(-1)] = value;
      return next;
    });
    setDirty(true);
  };

  const tryMirrorToBase = async (payload) => {
    if (!school?.id) return;
    // Attempt to mirror key fields to /schools/{id}; catch permissions silently
    const basePayload = {
      name: payload.schoolDisplayName || baseSchool?.name || "",
      logoURL: payload.logoURL || "",
      website: payload.website || "",
      phone: payload.phone || "",
      email: payload.email || "",
      address: payload.address || "",
      updatedAt: serverTimestamp(),
    };
    try {
      await updateDoc(doc(db, "schools", school.id), basePayload);
      return true;
    } catch (err) {
      // Most likely permission denied if admins can't write /schools
      console.info("Mirror to base school doc skipped:", err?.message || err);
      return false;
    }
  };

  const savePrefs = async () => {
    if (!school?.id) return;
    setSaving(true);
    const ref = doc(db, "schools", school.id, "settings", "prefs");
    const payload = {
      ...prefs,
      academicYearStart: prefs.academicYearStart || null,
      academicYearEnd: prefs.academicYearEnd || null,
      updatedAt: serverTimestamp(),
      createdAt: prefs.createdAt || serverTimestamp(),
      schoolId: school.id,
    };

    const prev = prefs;
    try {
      await setDoc(ref, payload, { merge: true });

      // Optional mirror to base school doc
      const mirrored = await tryMirrorToBase(payload);

      setDirty(false);
      await Swal.fire({
        icon: "success",
        title: mirrored ? "Settings saved & profile updated" : "Settings saved",
        text: mirrored ? "" : "Note: Base school profile not changed (insufficient permission).",
        timer: 1200,
        showConfirmButton: false,
      });
    } catch (err) {
      setPrefs(prev);
      Swal.fire({ icon: "error", title: "Save failed", text: err?.message || "Network error" });
    } finally {
      setSaving(false);
    }
  };

  /* ----- Logo upload ----- */
  const [uploading, setUploading] = useState(false);
  const [uploadPct, setUploadPct] = useState(0);
  const fileRef = useRef(null);

const uploadLogo = () => {
  const f = fileRef.current?.files?.[0];
  const sid = school?.id;
  if (!f || !sid) return;

  // simple validation
  if (!/^image\/(png|jpe?g|webp)$/i.test(f.type)) {
    return Swal.fire({ icon: "info", title: "Pick a PNG / JPG / WEBP image" });
  }
  if (f.size > 3 * 1024 * 1024) {
    return Swal.fire({ icon: "info", title: "Image too large (max ~3MB)" });
  }

  const safeName = f.name.replace(/[^a-zA-Z0-9._-]+/g, "_");
  const path = `schools/${sid}/branding/logo_${Date.now()}_${safeName}`;
  const ref = sRef(storage, path);

  const task = uploadBytesResumable(ref, f, {
    contentType: f.type,
    cacheControl: "public,max-age=86400"
  });

  setUploading(true);
  setUploadPct(0);

  task.on(
    "state_changed",
    snap => setUploadPct(Math.round((snap.bytesTransferred / snap.totalBytes) * 100)),
    err => {
      setUploading(false);
      setUploadPct(0);
      Swal.fire({ icon: "error", title: "Upload failed", text: err?.message || "Storage error" });
    },
    async () => {
      try {
        const url = await getDownloadURL(task.snapshot.ref);

        // write both a URL and a path (works with your StudentLayout resolver)
        await setDoc(doc(db, "schools", sid), {
          logoURL: url,       // camel-case version you already use
          logoUrl: url,       // also save lower-camel in case other code reads this
          logoPath: path,     // storage path for later re-resolution if needed
          updatedAt: serverTimestamp()
        }, { merge: true });

        // reflect in local UI
        change("logoURL", url);
        change("logoUrl", url);

        Swal.fire({ icon: "success", title: "Logo uploaded", timer: 900, showConfirmButton: false });
      } finally {
        setUploading(false);
        setUploadPct(100);
        if (fileRef.current) fileRef.current.value = "";
      }
    }
  );
};

  /* ----- Classes manager (schools/{id}/classes) ----- */
  const [classesRows, setClassesRows] = useState([]);
  const [loadingClasses, setLoadingClasses] = useState(true);
  const [manageClass, setManageClass] = useState(null); // className for modal

  useEffect(() => {
    if (!school?.id) return;
    setLoadingClasses(true);
    const col = collection(db, "schools", school.id, "classes");
    const unsub = onSnapshot(query(col, orderBy("className", "asc"), orderBy("section", "asc")), (snap) => {
      setClassesRows(snap.docs.map(d => ({ id: d.id, ...(d.data() || {}) })));
      setLoadingClasses(false);
    }, () => setLoadingClasses(false));
    return () => unsub();
  }, [school?.id]);

  const classesGrouped = useMemo(() => {
    const map = new Map();
    classesRows.forEach(r => {
      if (!map.has(r.className)) map.set(r.className, new Set());
      if (r.section) map.get(r.className).add(r.section);
    });
    return Array.from(map.entries()).map(([className, set]) => ({
      className,
      sections: Array.from(set).sort(),
    })).sort((a,b) => a.className.localeCompare(b.className));
  }, [classesRows]);

  // Quick add single class-section
  const [newClass, setNewClass] = useState("");
  const [newSec, setNewSec] = useState("");
  const [addingCS, setAddingCS] = useState(false);

  const addClassSection = async () => {
    const c = newClass.trim() || "";
    const s = newSec.trim() || "";
    if (!c) return Swal.fire({ icon: "warning", title: "Class is required" });
    setAddingCS(true);
    try {
      if (classesRows.some(r => r.className === c && r.section === s)) {
        Swal.fire({ icon: "info", title: "Already exists" });
      } else {
        await addDoc(collection(db, "schools", school.id, "classes"), {
          className: c,
          section: s,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
        });
        setNewClass(""); setNewSec("");
      }
    } finally {
      setAddingCS(false);
    }
  };

  const seedDefaults = async () => {
    const ok = await Swal.fire({
      icon: "question",
      title: "Seed classes?",
      text: "This will add standard classes (Class 1–12) with sections A–E (skips existing).",
      showCancelButton: true
    });
    if (!ok.isConfirmed) return;

    const batch = writeBatch(db);
    let adds = 0;
    CLASS_FALLBACK.forEach(c => {
      SECTION_FALLBACK.forEach(s => {
        const exists = classesRows.some(r => r.className === c && r.section === s);
        if (!exists) {
          const ref = doc(collection(db, "schools", school.id, "classes"));
          batch.set(ref, {
            className: c, section: s,
            createdAt: serverTimestamp(), updatedAt: serverTimestamp()
          });
          adds += 1;
        }
      });
    });

    if (adds === 0) {
      Swal.fire({ icon: "info", title: "Nothing to add", text: "All defaults already present." });
      return;
    }
    await batch.commit();
    Swal.fire({ icon: "success", title: `Added ${adds} records` });
  };

  const deleteWholeClass = async (className) => {
    const ok = await Swal.fire({
      icon: "warning",
      title: `Delete ${className}?`,
      text: "This removes all its sections from Classes (students are NOT deleted).",
      showCancelButton: true, confirmButtonColor: "#d33", confirmButtonText: "Delete"
    });
    if (!ok.isConfirmed) return;

    const snap = await getDocs(
      query(collection(db, "schools", school.id, "classes"), where("className", "==", className))
    );
    const batch = writeBatch(db);
    snap.forEach(d => batch.delete(d.ref));
    await batch.commit();
    Swal.fire({ icon: "success", title: "Class removed" });
  };

  /* ----- UI ----- */
  if (loadingSchool) {
    return (
      <Card className="border-0 shadow-sm">
        <Card.Body className="py-5 text-center text-muted">
          <Spinner animation="border" size="sm" /> Loading…
        </Card.Body>
      </Card>
    );
  }
  if (!school?.id) return <div className="text-muted">No school selected.</div>;

  return (
    <Container fluid className="p-0">
      <Row className="mb-3 g-2 align-items-center">
        <Col>
          <h5 className="mb-0">School Settings</h5>
          <small className="text-muted">Manage preferences for {school.name}</small>
        </Col>
        <Col className="text-end">
          <Button onClick={savePrefs} disabled={!canEdit || saving || !dirty}>
            {saving ? (<><Spinner animation="border" size="sm" className="me-1" /> Saving…</>) : (<>Save Changes</>)}
          </Button>
        </Col>
      </Row>

      {/* Base school doc preview (for clarity) */}
      <Card className="mb-3 border-0 shadow-sm">
        <Card.Body>
          <Row className="g-3 align-items-center">
            <Col md="auto">
              <div className="bg-light border rounded d-flex align-items-center justify-content-center"
                   style={{ width: 72, height: 72 }}>
                { (prefs.logoURL || baseSchool?.logoURL) ? (
                  <img
                    src={prefs.logoURL || baseSchool?.logoURL}
                    alt="logo"
                    style={{ maxWidth: "100%", maxHeight: "100%", objectFit: "contain" }}
                  />
                ) : <span className="text-muted small">No logo</span> }
              </div>
            </Col>
            <Col>
              <div className="fw-semibold">
                {(prefs.schoolDisplayName || baseSchool?.name) || "Untitled school"}
              </div>
              <div className="small text-muted">
                {baseSchool?.website && <span className="me-2">{new URL(baseSchool.website).hostname}</span>}
                {baseSchool?.phone && <Badge bg="light" text="dark" className="me-2 border">{baseSchool.phone}</Badge>}
                {baseSchool?.email && <Badge bg="light" text="dark" className="me-2 border">{baseSchool.email}</Badge>}
              </div>
              {baseSchool?.address && <div className="small text-muted mt-1">{baseSchool.address}</div>}
            </Col>
            <Col md="auto">
              <Badge bg="light" text="dark" className="border">ID: {school.id}</Badge>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      <Card className="p-3">
        <Tabs defaultActiveKey="general" id="school-settings-tabs" className="mb-3">
          {/* General */}
          <Tab eventKey="general" title="General">
            {(loadingPrefs || loadingBase) ? (
              <div className="py-4 text-center text-muted"><Spinner animation="border" size="sm"/> Loading…</div>
            ) : (
              <Row className="g-3">
                <Col md={6}>
                  <Form.Label>Display name</Form.Label>
                  <Form.Control
                    value={prefs.schoolDisplayName || ""}
                    onChange={(e) => change("schoolDisplayName", e.target.value)}
                    disabled={!canEdit}
                  />
                  
                </Col>
                <Col md={6}>
                  <Form.Label>Website</Form.Label>
                  <InputGroup>
                    <InputGroup.Text>https://</InputGroup.Text>
                    <Form.Control
                      value={(prefs.website || "").replace(/^https?:\/\//, "")}
                      onChange={(e) => change("website", e.target.value ? `https://${e.target.value.replace(/^https?:\/\//, "")}` : "")}
                      disabled={!canEdit}
                    />
                  </InputGroup>
                </Col>

                <Col md={4}>
                  <Form.Label>Phone</Form.Label>
                  <Form.Control
                    value={prefs.phone || ""}
                    onChange={(e) => change("phone", e.target.value)}
                    disabled={!canEdit}
                  />
                </Col>
                <Col md={4}>
                  <Form.Label>Email</Form.Label>
                  <Form.Control
                    type="email"
                    value={prefs.email || ""}
                    onChange={(e) => change("email", e.target.value)}
                    disabled={!canEdit}
                  />
                </Col>
                <Col md={4}>
                  <Form.Label>Academic year</Form.Label>
                  <InputGroup>
                    <Form.Control
                      type="date"
                      value={prefs.academicYearStart ? dayjs(prefs.academicYearStart).format("YYYY-MM-DD") : ""}
                      onChange={(e) => change("academicYearStart", e.target.value ? new Date(e.target.value) : null)}
                      disabled={!canEdit}
                    />
                    <InputGroup.Text>to</InputGroup.Text>
                    <Form.Control
                      type="date"
                      value={prefs.academicYearEnd ? dayjs(prefs.academicYearEnd).format("YYYY-MM-DD") : ""}
                      onChange={(e) => change("academicYearEnd", e.target.value ? new Date(e.target.value) : null)}
                      disabled={!canEdit}
                    />
                  </InputGroup>
                </Col>

                <Col md={12}>
                  <Form.Label>Address</Form.Label>
                  <Form.Control
                    as="textarea" style={{ height: 90 }}
                    value={prefs.address || ""}
                    onChange={(e) => change("address", e.target.value)}
                    disabled={!canEdit}
                  />
                </Col>
              </Row>
            )}
          </Tab>

          {/* Branding */}
          <Tab eventKey="branding" title="Branding">
            <Row className="g-3">
              <Col md={6}>
                <Form.Label>Logo</Form.Label>
                <div className="d-flex align-items-center gap-3">
                  <div className="border rounded p-2 bg-light" style={{ width: 120, height: 120, display: "flex", alignItems: "center", justifyContent: "center" }}>
                    {prefs.logoURL ? (
                      <img src={prefs.logoURL} alt="logo" style={{ maxWidth: "100%", maxHeight: "100%", objectFit: "contain" }} />
                    ) : (
                      <span className="text-muted small">No logo</span>
                    )}
                  </div>
                  <div style={{ flex: 1 }}>
                    <InputGroup className="mb-2">
                      <Form.Control value={prefs.logoURL || "dsds"} onChange={(e) => change("logoURL", e.target.value)} disabled={!canEdit}/>
                      <Button variant="outline-secondary" onClick={() => { change("logoURL",""); setDirty(true);} } disabled={!canEdit}>Remove</Button>
                    </InputGroup>
                    <div className="d-flex gap-2">
                      <Form.Control type="file" ref={fileRef} accept="image/*" disabled={!canEdit || uploading}/>
                      <Button onClick={uploadLogo} disabled={!canEdit || uploading}>{uploading ? "Uploading…" : "Upload"}</Button>
                    </div>
                    {uploading && <ProgressBar className="mt-2" now={uploadPct} label={`${uploadPct}%`}/>}
                  </div>
                </div>
                <div className="form-text">Stored at <code>/schools/{school?.id}/branding</code> in Firebase Storage.</div>
              </Col>
            </Row>
          </Tab>

          {/* Attendance */}
          <Tab eventKey="attendance" title="Attendance">
            <Row className="g-3">
              <Col md={4}>
                <Form.Label>Marking window</Form.Label>
                <InputGroup>
                  <Form.Control
                    type="time"
                    value={prefs.attendance?.markStart || "08:00"}
                    onChange={(e) => change("attendance.markStart", e.target.value)}
                    disabled={!canEdit}
                  />
                  <InputGroup.Text>to</InputGroup.Text>
                  <Form.Control
                    type="time"
                    value={prefs.attendance?.markEnd || "14:00"}
                    onChange={(e) => change("attendance.markEnd", e.target.value)}
                    disabled={!canEdit}
                  />
                </InputGroup>
              </Col>
              <Col md={4}>
                <Form.Label>Edit lock (days)</Form.Label>
                <Form.Control
                  type="number" min={0} max={30}
                  value={prefs.attendance?.lockDays ?? 2}
                  onChange={(e) => change("attendance.lockDays", clamp(parseInt(e.target.value || "0",10),0,30))}
                  disabled={!canEdit}
                />
                <div className="form-text">Marking older than this many days becomes read-only.</div>
              </Col>
              <Col md={4}>
                <Form.Label>Enabled statuses</Form.Label>
                <div className="d-flex flex-wrap gap-2">
                  {STATUSES.map(s => {
                    const enabled = (prefs.attendance?.statuses || []).includes(s.key);
                    return (
                      <Form.Check
                        key={s.key}
                        type="checkbox"
                        id={`st-${s.key}`}
                        label={<Badge bg={s.variant}>{s.label}</Badge>}
                        checked={enabled}
                        disabled={!canEdit}
                        onChange={() => {
                          const cur = new Set(prefs.attendance?.statuses || []);
                          cur.has(s.key) ? cur.delete(s.key) : cur.add(s.key);
                          change("attendance.statuses", Array.from(cur));
                        }}
                      />
                    );
                  })}
                </div>
              </Col>

              <Col md={12}>
                <Form.Label>Working days</Form.Label>
                <div className="d-flex flex-wrap gap-3">
                  {["Sun","Mon","Tue","Wed","Thu","Fri","Sat"].map((d,i) => {
                    const active = (prefs.attendance?.workingDays || []).includes(i);
                    return (
                      <Form.Check
                        key={i}
                        type="checkbox"
                        id={`wd-${i}`}
                        label={d}
                        checked={active}
                        disabled={!canEdit}
                        onChange={() => {
                          const set = new Set(prefs.attendance?.workingDays || []);
                          set.has(i) ? set.delete(i) : set.add(i);
                          change("attendance.workingDays", Array.from(set).sort((a,b)=>a-b));
                        }}
                      />
                    );
                  })}
                </div>
              </Col>
            </Row>
          </Tab>

          {/* Online classes */}
          <Tab eventKey="online" title="Online Classes">
            <Row className="g-3">
              <Col md={4}>
                <Form.Label>Default platform</Form.Label>
                <Form.Select
                  value={prefs.online?.defaultPlatform || "meet"}
                  onChange={(e) => change("online.defaultPlatform", e.target.value)}
                  disabled={!canEdit}
                >
                  <option value="meet">Google Meet</option>
                  <option value="zoom">Zoom</option>
                  <option value="teams">Microsoft Teams</option>
                  <option value="other">Other</option>
                </Form.Select>
              </Col>
            </Row>
          </Tab>

          {/* Communication */}
          <Tab eventKey="comms" title="Circulars & Notifications">
            <Row className="g-3">
              <Col md={4}>
                <Form.Check
                  type="switch"
                  id="allow-teacher-circ"
                  label="Allow teachers to create circulars"
                  checked={!!prefs.comms?.allowTeacherCirculars}
                  disabled={!canEdit}
                  onChange={(e) => change("comms.allowTeacherCirculars", e.target.checked)}
                />
                <Form.Check
                  type="switch"
                  id="require-ack"
                  label="Require acknowledgment from recipients"
                  checked={!!prefs.comms?.requireAck}
                  disabled={!canEdit}
                  onChange={(e) => change("comms.requireAck", e.target.checked)}
                />
              </Col>
              <Col md={4}>
                <Form.Check
                  type="switch"
                  id="enable-push"
                  label="Enable push notifications"
                  checked={!!prefs.comms?.enablePush}
                  disabled={!canEdit}
                  onChange={(e) => change("comms.enablePush", e.target.checked)}
                />
                <Form.Check
                  type="switch"
                  id="enable-email"
                  label="Enable email notifications"
                  checked={!!prefs.comms?.enableEmail}
                  disabled={!canEdit}
                  onChange={(e) => change("comms.enableEmail", e.target.checked)}
                />
              </Col>
              <Col md={4}>
                <Form.Label>Suggested tags</Form.Label>
                <InputGroup>
                  <Form.Control
                    placeholder="comma,separated,tags"
                    value={(prefs.tagsSuggested || []).join(",")}
                    onChange={(e) => change("tagsSuggested", e.target.value.split(",").map(s => s.trim()).filter(Boolean))}
                    disabled={!canEdit}
                  />
                </InputGroup>
                <div className="mt-2 d-flex flex-wrap gap-2">
                  {(prefs.tagsSuggested || []).map(t => <Badge key={t} bg="light" text="dark" className="border">{t}</Badge>)}
                </div>
              </Col>
            </Row>
          </Tab>

          {/* Classes & Sections */}
          <Tab eventKey="classes" title="Classes & Sections">
            <Row className="g-2 align-items-end mb-3">
              <Col md={4}>
                <Form.Label>Class</Form.Label>
                <Form.Control
                  placeholder="e.g., Class 9"
                  value={newClass}
                  onChange={(e) => setNewClass(e.target.value)}
                  disabled={!canEdit}
                />
              </Col>
              <Col md={3}>
                <Form.Label>Section</Form.Label>
                <Form.Control
                  placeholder="(optional) e.g., A"
                  value={newSec}
                  onChange={(e) => setNewSec(e.target.value)}
                  disabled={!canEdit}
                />
              </Col>
              <Col md={5} className="text-end">
                <ButtonGroup>
                  <Button onClick={addClassSection} disabled={!canEdit || addingCS}>
                    {addingCS ? "Adding…" : "Add"}
                  </Button>
                  <Button variant="outline-secondary" onClick={seedDefaults} disabled={!canEdit}>
                    Seed defaults
                  </Button>
                </ButtonGroup>
              </Col>
            </Row>

            <div className="table-responsive">
              <Table hover className="align-middle">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Class</th>
                    <th>Sections</th>
                    <th className="text-end">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {loadingClasses ? (
                    <tr><td colSpan={4} className="text-center py-4"><Spinner animation="border" size="sm" /> Loading…</td></tr>
                  ) : classesGrouped.length === 0 ? (
                    <tr><td colSpan={4} className="text-center text-muted py-4">No classes yet</td></tr>
                  ) : (
                    classesGrouped.map((r, idx) => (
                      <tr key={r.className}>
                        <td>{idx+1}</td>
                        <td className="fw-semibold">{r.className}</td>
                        <td>
                          <div className="d-flex flex-wrap gap-2">
                            {r.sections.length ? r.sections.map(s => (
                              <Badge key={s} bg="light" text="dark" className="border">{s}</Badge>
                            )) : <span className="text-muted">—</span>}
                          </div>
                        </td>
                        <td className="text-end">
                          <Button
                            size="sm"
                            variant="outline-secondary"
                            className="me-2"
                            onClick={() => setManageClass(r.className)}
                            disabled={!canEdit}
                          >
                            <i className="bi bi-pencil" /> Manage
                          </Button>
                          <Button
                            size="sm"
                            variant="outline-danger"
                            onClick={() => deleteWholeClass(r.className)}
                            disabled={!canEdit}
                          >
                            <i className="bi bi-trash" /> Delete class
                          </Button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </Table>
            </div>
          </Tab>
        </Tabs>
      </Card>

      <ManageSectionsModal
        show={!!manageClass}
        onHide={() => setManageClass(null)}
        schoolId={school?.id}
        className={manageClass || ""}
      />

      <style>{`
        .card { border-radius: 14px; }
        .form-control, .form-select { border-radius: 10px; }
      `}</style>
    </Container>
  );
}
